import { useState } from "react";
import { buildFundsOnHand, isActive, portalLabel } from "../../services/payrollFinanceCalculator";
import { recordUse, rejectUse } from "../../services/payrollFinanceService";
import { PortalSelect, accountSuggestions, dateLabel, money, todayIso } from "./shared";

const emptyForm = () => ({ date: todayIso(), time: "", companyAccount: "", payee: "", purpose: "", amount: "", portal: "", reference: "", note: "" });

export default function UseOfFundsTab({ data, reload, notify }) {
  const [form, setForm] = useState(null);
  const set = (field) => (e) => setForm((current) => ({ ...current, [field]: e.target.value }));
  const funds = buildFundsOnHand({ movements: data.movements.filter(isActive), uses: data.uses });
  const uses = [...data.uses].sort((a, b) => `${b.date} ${b.time || ""}`.localeCompare(`${a.date} ${a.time || ""}`));
  const selectedAccount = form ? funds.accounts.find((a) => a.account === form.companyAccount.trim()) : null;
  const afterThis = selectedAccount && Number(form.amount) > 0 ? selectedAccount.onHand - Number(form.amount) : null;

  const submit = async (event) => {
    event.preventDefault();
    try { await recordUse(form); notify("Use of funds recorded."); setForm(null); await reload(); }
    catch (err) { notify(err.message || "Could not record this entry.", true); }
  };

  const reject = async (row) => {
    const reason = window.prompt(`Reason for rejecting this ${money(row.amount)} entry:`);
    if (reason === null) return;
    try { await rejectUse(row, reason); notify("Entry rejected."); await reload(); }
    catch (err) { notify(err.message || "Could not reject.", true); }
  };

  return (
    <>
      <section className="panel">
        <div className="panel-header"><h2 className="panel-title">Funds on hand by ALM account</h2></div>
        <div className="panel-body table-wrap">
          <table className="data-table">
            <thead><tr><th>ALM account</th><th>Received from employees</th><th>Used by company</th><th>On hand</th></tr></thead>
            <tbody>
              {funds.accounts.map((a) => (
                <tr key={a.account}><td>{a.account}</td><td>{money(a.received)}</td><td>{money(a.used)}</td>
                  <td><strong style={a.onHand < 0 ? { color: "var(--danger)" } : undefined}>{money(a.onHand)}</strong>{a.onHand < 0 && <div style={{ fontSize: 12 }}>more used than received — check entries</div>}</td></tr>
              ))}
              {funds.accounts.length > 0 && <tr style={{ fontWeight: 700 }}><td>Total</td><td>{money(funds.received)}</td><td>{money(funds.used)}</td><td>{money(funds.onHand)}</td></tr>}
              {!funds.accounts.length && <tr><td colSpan={4}>Nothing received into an ALM account yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>

      <section className="panel">
        <div className="panel-header">
          <h2 className="panel-title">Where the funds were used</h2>
          <button type="button" className="btn btn-primary" onClick={() => setForm(emptyForm())}>Record Use of Funds</button>
        </div>
        {form && (
          <form className="panel-body" onSubmit={submit}>
            <div className="form-grid">
              <div className="field"><label>Date</label><input className="input" type="date" value={form.date} onChange={set("date")} /></div>
              <div className="field"><label>Time</label><input className="input" type="time" value={form.time} onChange={set("time")} /></div>
              <div className="field"><label>Paid from ALM account</label>
                <input className="input" list="pf-use-accounts" value={form.companyAccount} onChange={set("companyAccount")} placeholder="Same name as the account that received the money" />
                <datalist id="pf-use-accounts">{accountSuggestions(data).map((name) => <option key={name} value={name} />)}</datalist>
              </div>
              <div className="field"><label>Amount (PKR)</label><input className="input" type="number" min="0.01" step="0.01" value={form.amount} onChange={set("amount")} /></div>
              <div className="field field-wide"><label>What it was used for</label><input className="input" value={form.purpose} onChange={set("purpose")} placeholder="e.g. September salaries — CBRE Karachi staff" /></div>
              <div className="field"><label>Paid to (payee)</label><input className="input" value={form.payee} onChange={set("payee")} /></div>
              <PortalSelect value={form.portal} onChange={(value) => setForm({ ...form, portal: value })} allowNone label="Portal it relates to (optional)" />
              <div className="field"><label>Reference</label><input className="input" value={form.reference} onChange={set("reference")} /></div>
              <div className="field field-wide"><label>Note</label><input className="input" value={form.note} onChange={set("note")} /></div>
            </div>
            {afterThis !== null && <p className="page-copy" style={afterThis < 0 ? { color: "var(--danger)" } : undefined}>{money(selectedAccount.onHand)} on hand in this account; {money(afterThis)} after this entry{afterThis < 0 ? " — more than was received into it." : "."}</p>}
            <div className="button-row" style={{ marginTop: 16 }}>
              <button className="btn btn-primary" type="submit">Save</button>
              <button className="btn btn-muted" type="button" onClick={() => setForm(null)}>Cancel</button>
            </div>
          </form>
        )}
        <div className="panel-body table-wrap">
          <table className="data-table">
            <thead><tr><th>Date / time</th><th>ALM account</th><th>Used for</th><th>Paid to</th><th>Portal</th><th>Reference</th><th>Amount</th><th /></tr></thead>
            <tbody>
              {uses.map((u) => {
                const rejected = !isActive(u);
                return (
                  <tr key={u.id} style={rejected ? { opacity: 0.55, textDecoration: "line-through" } : undefined}>
                    <td>{dateLabel(u.date, u.time)}</td><td>{u.companyAccount}</td>
                    <td>{u.purpose}{u.note && <div className="page-copy" style={{ fontSize: 12 }}>{u.note}</div>}{rejected && <div style={{ fontSize: 12, textDecoration: "none" }}>Rejected: {u.rejectedReason}</div>}</td>
                    <td>{u.payee || "—"}</td><td>{u.portal ? portalLabel(u.portal) : "—"}</td><td>{u.reference || "—"}</td><td>{money(u.amount)}</td>
                    <td>{!rejected && <button className="btn btn-danger" type="button" onClick={() => reject(u)}>Reject</button>}</td>
                  </tr>
                );
              })}
              {!uses.length && <tr><td colSpan={8}>No use of funds recorded yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}
